import {useMemo, useState, useEffect} from 'react'
import isEqual from 'lodash.isequal'
import {useSelector} from 'react-redux'
import {selectSettingsUsers} from 'features/Settings'
import {
  selectCloudAppHasUnsavedChanges,
  selectPolicies,
} from 'features/Policies/policiesSelectors'
import {
  selectedEndpointDebugMode,
  selectEndpoints,
} from 'features/Endpoints/endpointsSelectors'
import {EndpointsConfigTypes, ValidUserTypes} from 'interfaces'
import {selectUser} from 'features/User'
import {queryClient} from 'services'
import {
  useSnapshotContext,
  SETTINGS_ENDPOINT_CONFIG_SNAPSHOT,
} from 'context/SnapshotContext'

type ActionType = {
  isSnackbarActionAvailable: boolean
  doIHaveAccess: boolean
  isUserLoggedIn: boolean
}

export const useActions = (): ActionType => {
  const {policyAssignee} = useSelector(selectPolicies)
  const hasCloudAppChanges = useSelector(selectCloudAppHasUnsavedChanges)
  const {role, current} = useSelector(selectUser)
  const {snapshot} = useSnapshotContext()

  const doIHaveAccess = useMemo(() => {
    return role === ValidUserTypes.Administrator
  }, [role])

  const {blockPages, defaultBlockPagePayload} = useSelector(selectSettingsUsers)
  const {selectedEndpoints} = useSelector(selectEndpoints)
  const isDebugMode = useSelector(selectedEndpointDebugMode)

  const isUserLoggedIn = useMemo(() => {
    return Boolean(current?.token)
  }, [current])

  const endpointSettingsConfigSnapshot = snapshot?.[
    SETTINGS_ENDPOINT_CONFIG_SNAPSHOT
  ] as EndpointsConfigTypes

  const [latestEndpointSettingsData, setLatestEndpointSettingsData] = useState<
    EndpointsConfigTypes | undefined
  >(
    () =>
      queryClient.getQueryData<{data: EndpointsConfigTypes}>([
        'getEndpointsConfig',
      ])?.data ?? endpointSettingsConfigSnapshot,
  )

  useEffect(() => {
    const unsubscribe = queryClient
      .getQueryCache()
      .subscribe(({type, query}) => {
        // Observer notifications can fire while another component is rendering.
        if (type === 'updated' && query.queryKey[0] === 'getEndpointsConfig') {
          const updatedCacheData = queryClient.getQueryData<{
            data: EndpointsConfigTypes
          }>(query.queryKey)
          setLatestEndpointSettingsData(updatedCacheData?.data)
        }
      })
    return () => unsubscribe()
  }, [queryClient])

  const isSnackbarActionAvailable = useMemo(() => {
    return (
      isDebugMode ||
      !isEqual(policyAssignee?.data, policyAssignee?.snapshot) ||
      (!isEqual(latestEndpointSettingsData, endpointSettingsConfigSnapshot) &&
        endpointSettingsConfigSnapshot?.requireDelegatedAuth) ||
      !isEqual(
        blockPages?.data?.pages?.default,
        blockPages?.snapshot?.pages?.default,
      ) ||
      (endpointSettingsConfigSnapshot &&
        !isEqual(latestEndpointSettingsData, endpointSettingsConfigSnapshot)) ||
      Boolean(defaultBlockPagePayload?.defaultPages?.length) ||
      (blockPages?.updatedData?.addCustomPages ?? []).length > 0 ||
      (blockPages?.updatedData?.deleteCustomPages ?? []).length > 0 ||
      hasCloudAppChanges
    )
  }, [
    policyAssignee,
    latestEndpointSettingsData,
    endpointSettingsConfigSnapshot,
    blockPages,
    selectedEndpoints,
    defaultBlockPagePayload,
    hasCloudAppChanges,
    role,
  ])

  return {
    isSnackbarActionAvailable,
    doIHaveAccess,
    isUserLoggedIn,
  }
}
